<script setup lang="ts">
import { useI18n } from "vue-i18n";

import ActionWithTooltip from "@/components/ActionWithTooltip.vue";
import InflectionSchemeLink from "@/components/article/InflectionSchemeLink.vue";
import Tooltip from "@/components/article/Tooltip.vue";
import TouchOrMouseElement from "@/components/TouchOrMouseElement.vue";
import vLang from "@/directives/lang";
import { MatomoHelper } from "@/helpers";
import { MatomoEventAction, MatomoEventCategory } from "@/types";
import type { GyldendalTranslationContent } from "@/types/article-formats/gyldendal";

defineProps<{
  item: GyldendalTranslationContent;
  articleId: number;
  sectionId: number;
  textClass?: string;
}>();

const { t } = useI18n();

const trackInflectionSchemeClick = () => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.Results,
    MatomoEventAction.ClickInflectionScheme,
    "Inflection scheme on translation"
  );
};
</script>

<template>
  <span
    v-if="item.type === 'translation'"
    v-lang:target
    :class="textClass"
    v-html="item.content"
  ></span>
  <template v-else-if="item.type === 'lemma'">
    <TouchOrMouseElement
      class="ml-1 d-inline-flex article-content__inflection-scheme article-content__inflection-scheme--gyldendal"
    >
      <template #mouse>
        <ActionWithTooltip hide-on-click>
          <template #element>
            <InflectionSchemeLink
              :word="item.content"
              :article-id="articleId"
              :translation-id="item.id"
              :section-id="sectionId"
              @click="trackInflectionSchemeClick()"
            ></InflectionSchemeLink>
          </template>
          <template #actionInitial>{{ t("article.actions.conjugation.open") }}</template>
        </ActionWithTooltip>
      </template>
      <template #touch>
        <span class="d-inline-flex align-items-center">
          <InflectionSchemeLink
            :word="item.content"
            :article-id="articleId"
            :translation-id="item.id"
            :section-id="sectionId"
            @click="trackInflectionSchemeClick()"
          ></InflectionSchemeLink>
        </span>
      </template>
    </TouchOrMouseElement>
  </template>
  <span v-else-if="item.type === 'grammar'" v-lang:target
    >&nbsp;(<span v-html="item.content" />)</span
  >
  <sub
    v-else-if="item.type === 'subscript'"
    v-lang:target
    class="mark-symbol"
    v-html="item.content"
  ></sub>
  <sup v-else-if="item.type === 'superscript'" v-lang:target v-html="item.content"></sup>
  <span v-else-if="item.type === 'mark'" v-lang:target class="mark-symbol"
    >&nbsp;<span v-html="item.content"></span
  ></span>
  <span v-else-if="item.type === 'info'">&nbsp;<Tooltip :text="item.content"></Tooltip></span>
</template>
